/**
 * contact-profile-menu.tsx — Bấm tên/avatar một người (thành viên nhóm, hoặc khách ở đầu hội thoại
 * 1-1) → menu hành động giống Zalo gốc: Xem trang cá nhân, Nhắn tin riêng, Thêm bạn/Hủy kết bạn.
 *
 * Chỉ áp dụng cho tài khoản ZALO CÁ NHÂN (accountId) — các API kết bạn/xem trạng thái chỉ Zalo cá
 * nhân có (`friend-routes.ts`). "Nhắn tin riêng" dùng lại `/conversations/start` (mode 'uid') — đã
 * biết sẵn uid/tên/ảnh nên không tốn lượt tra Zalo.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { MessageCircle, UserPlus, UserMinus, Loader2 } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { api, apiError } from '@/lib/api-client'
import { safeHttpUrl } from '@/lib/safe-url'
import { initials } from '@/lib/utils'
import { useStartConversation } from '@/hooks/use-conversations'

interface FriendStatus {
  isFriend: boolean
  isRequested: boolean
  isRequesting: boolean
}

export function ContactProfileMenu({
  accountId,
  uid,
  name,
  avatarUrl,
  showMessageAction = true,
  children,
}: {
  /** channelAccountId của tài khoản Zalo cá nhân đang xem — không phải uid người được bấm. */
  accountId: string
  uid: string
  name: string
  avatarUrl?: string | null
  /** Tắt "Nhắn tin riêng" khi đang đứng NGAY trong hội thoại 1-1 với chính người này. */
  showMessageAction?: boolean
  children: React.ReactNode
}) {
  const { t } = useTranslation('convContact')
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const start = useStartConversation()

  const status = useQuery<FriendStatus>({
    queryKey: ['zalo-friend-status', accountId, uid],
    queryFn: async () => {
      const { data } = await api.get<FriendStatus>(`/zalo-accounts/${accountId}/friends/${uid}/status`)
      return data
    },
    enabled: open,
    staleTime: 30_000,
  })

  async function handleMessage() {
    try {
      const { conversationId } = await start.mutateAsync({ accountId, mode: 'uid', uid, name, avatar: avatarUrl })
      setOpen(false)
      navigate(`/conversations/${conversationId}`)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleAddFriend() {
    try {
      await api.post(`/zalo-accounts/${accountId}/friends/requests`, { userId: uid })
      toast.success(t('profileMenu.requestSent', { name }))
      qc.invalidateQueries({ queryKey: ['zalo-friend-status', accountId, uid] })
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleRemoveFriend() {
    if (!window.confirm(t('profileMenu.confirmUnfriend', { name }))) return
    try {
      await api.delete(`/zalo-accounts/${accountId}/friends/${uid}`)
      toast.success(t('profileMenu.unfriended', { name }))
      qc.invalidateQueries({ queryKey: ['zalo-friend-status', accountId, uid] })
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar className="h-9 w-9">
            {safeHttpUrl(avatarUrl) && <AvatarImage src={safeHttpUrl(avatarUrl)} alt="" />}
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-[11px] text-muted-foreground">zlw{uid}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        {showMessageAction && (
          <DropdownMenuItem onClick={() => void handleMessage()} disabled={start.isPending}>
            {start.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
            {t('profileMenu.message')}
          </DropdownMenuItem>
        )}
        {status.isLoading ? (
          <DropdownMenuItem disabled>
            <Loader2 className="h-4 w-4 animate-spin" /> {t('profileMenu.loading')}
          </DropdownMenuItem>
        ) : status.data?.isFriend ? (
          <DropdownMenuItem onClick={() => void handleRemoveFriend()} className="text-destructive focus:text-destructive">
            <UserMinus className="h-4 w-4" /> {t('profileMenu.unfriend')}
          </DropdownMenuItem>
        ) : status.data?.isRequested ? (
          <DropdownMenuItem disabled>
            <UserPlus className="h-4 w-4" /> {t('profileMenu.requested')}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={() => void handleAddFriend()}>
            <UserPlus className="h-4 w-4" /> {t('profileMenu.addFriend')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
